import { useCallback, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { HLSMedia } from '../../helpers/hls-media';
import { WebRTCMedia } from '../../helpers/webrtc-media';

const useResumeMediaOnVisibilityChange = (videoEl: HTMLVideoElement | null) => {
    useEffect(() => {
        if (!videoEl) return;

        const handleVisibilityChange = () => {
            if (!document.hidden && videoEl.paused) {
                videoEl.play().catch((err) => {
                    console.error('Error resuming playback:', err);
                });
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [videoEl]);
};

interface CctvPlayerProps {
    hlsUrl: string;
    webrtcUrl?: string;
    className?: string;
    onData?: () => void;
    onError?: () => void;
}

const CctvCameraPlayer = (props: CctvPlayerProps) => {
    const { hlsUrl, webrtcUrl, className, onData, onError } = props;

    const videoRef = useRef<HTMLVideoElement>(null);
    const webRTCMediaRef = useRef<WebRTCMedia>(null);
    const hlsMediaRef = useRef<HLSMedia>(null);
    const isOnlineRef = useRef<boolean | null>(null);

    // Store callbacks in refs to avoid triggering effects on every parent render
    const onDataRef = useRef(onData);
    const onErrorRef = useRef(onError);

    // Update refs when callbacks change (doesn't trigger effects)
    useEffect(() => {
        onDataRef.current = onData;
        onErrorRef.current = onError;
    }, [onData, onError]);

    // Only depend on actual stream URLs, not callbacks
    const initHlS = useCallback(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        hlsMediaRef.current = new HLSMedia({ src: hlsUrl });
        hlsMediaRef.current.init(videoElement);

        hlsMediaRef.current.on(HLSMedia.Events.DATA, () => {
            onDataRef.current?.();
            isOnlineRef.current = true;
        });

        hlsMediaRef.current.on(HLSMedia.Events.ERROR, () => {
            onErrorRef.current?.();
            isOnlineRef.current = false;
        });
    }, [hlsUrl]);

    const destroyHls = useCallback(() => {
        hlsMediaRef.current?.destroy();
        hlsMediaRef.current = null;
    }, []);

    const initWebRTC = useCallback(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        webRTCMediaRef.current = new WebRTCMedia({ src: webrtcUrl });
        webRTCMediaRef.current.init(videoElement);

        webRTCMediaRef.current.on(WebRTCMedia.Events.DATA, () => {
            onDataRef.current?.();
            isOnlineRef.current = true;
        });

        webRTCMediaRef.current.on(WebRTCMedia.Events.ERROR, () => {
            onErrorRef.current?.();
            isOnlineRef.current = false;
        });
    }, [webrtcUrl]);

    const destroyWebRTC = useCallback(() => {
        webRTCMediaRef.current?.destroy();
        webRTCMediaRef.current = null;
    }, []);

    // Only reconnect when the actual stream source changes
    const initMedia = useCallback(() => {
        // Guard: ensure video element exists
        if (!videoRef.current) return;

        // Destroy existing streams first
        destroyHls();
        destroyWebRTC();

        // Try to initialize new stream
        if (webrtcUrl) {
            initWebRTC();
        } else if (hlsUrl) {
            initHlS();
        }
    }, [hlsUrl, webrtcUrl, initHlS, initWebRTC, destroyHls, destroyWebRTC]);

    // Initialize stream only when URLs change, not when callbacks change
    useEffect(() => {
        initMedia();

        return () => {
            destroyHls();
            destroyWebRTC();
        };
    }, [hlsUrl, webrtcUrl, initMedia]);

    // Retry connection if offline (independent of callback changes)
    useEffect(() => {
        const intervalId = setInterval(() => {
            if (!document.hidden && isOnlineRef.current === false) {
                initMedia();
            }
        }, 10000);

        return () => {
            clearInterval(intervalId);
        };
    }, [initMedia]);

    useResumeMediaOnVisibilityChange(videoRef.current);

    return (
        <video
            className={twMerge('w-full h-full object-fill', className)}
            ref={videoRef}
            muted
            autoPlay
            playsInline
        />
    );
};

export default CctvCameraPlayer;
